import { ArrowDown } from '@phosphor-icons/react'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { useTranslation } from 'react-i18next'

const PULL_THRESHOLD_PX = 64
const MAX_VISUAL_PULL_PX = 104
const RESISTANCE = 0.55
const SETTLED_PULL_PX = 54
const RING_RADIUS = 16
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

export interface PullToRefreshProps {
  children: ReactNode
  onRefresh: () => void
  refreshing?: boolean
  disabled?: boolean
  className?: string
  testId: string
  pullLabelKey: string
  releaseLabelKey: string
  refreshingLabelKey: string
  /** Use the component root as the scroll container instead of the window. */
  contained?: boolean
  scrollRef?: RefObject<HTMLDivElement | null>
}

function atPageTop(element: HTMLElement | null, contained: boolean): boolean {
  if (contained && element) return element.scrollTop <= 0
  if (typeof window === 'undefined') return true
  return window.scrollY <= 0
}

/**
 * Touch pull-to-refresh for mobile layouts (D-053).
 *
 * Native non-passive touch listeners are required: React's synthetic touch
 * listeners are passive, so the previous implementation could not stop the
 * browser from scrolling the whole viewport while the finger moved. The
 * indicator follows the damped pull and its ring fills toward the threshold,
 * while the content is translated by the same distance so the affordance
 * never covers the first card.
 */
export function PullToRefresh({
  children,
  onRefresh,
  refreshing = false,
  disabled = false,
  className = '',
  testId,
  pullLabelKey,
  releaseLabelKey,
  refreshingLabelKey,
  contained = false,
  scrollRef,
}: PullToRefreshProps) {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement>(null)
  const startY = useRef<number | null>(null)
  const startX = useRef<number | null>(null)
  const distanceRef = useRef(0)
  const onRefreshRef = useRef(onRefresh)
  const [pull, setPull] = useState(0)
  const [armed, setArmed] = useState(false)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    onRefreshRef.current = onRefresh
  }, [onRefresh])

  // The gesture must be ready as soon as async content is painted. Waiting
  // for a passive effect can miss the first touch after a skeleton disappears.
  useLayoutEffect(() => {
    const element = rootRef.current
    if (!element) return

    function reset() {
      startY.current = null
      startX.current = null
      distanceRef.current = 0
      setDragging(false)
      setArmed(false)
      setPull(0)
    }

    function onTouchStart(event: TouchEvent) {
      if (disabled || refreshing || !atPageTop(element, contained)) {
        startY.current = null
        startX.current = null
        return
      }
      // A modal owns the gesture while it is open; do not move the page behind
      // its scrim.
      if (document.querySelector('[aria-modal="true"]')) {
        startY.current = null
        startX.current = null
        return
      }
      startY.current = event.touches[0]?.clientY ?? null
      startX.current = event.touches[0]?.clientX ?? null
    }

    function onTouchMove(event: TouchEvent) {
      if (startY.current === null) return
      const currentY = event.touches[0]?.clientY
      const currentX = event.touches[0]?.clientX
      if (currentY === undefined) return
      const delta = currentY - startY.current
      // A horizontal gesture belongs to the tab swipe. Bail out before the
      // pull state engages so the two gestures never fight each other.
      if (
        currentX !== undefined &&
        startX.current !== null &&
        delta > 0 &&
        Math.abs(currentX - startX.current) > Math.abs(delta)
      ) {
        startY.current = null
        startX.current = null
        distanceRef.current = 0
        setPull(0)
        setArmed(false)
        setDragging(false)
        return
      }
      if (delta <= 0) {
        distanceRef.current = 0
        setPull(0)
        setArmed(false)
        setDragging(false)
        return
      }
      if (!atPageTop(element, contained)) {
        startY.current = null
        startX.current = null
        distanceRef.current = 0
        setPull(0)
        setArmed(false)
        setDragging(false)
        return
      }
      if (event.cancelable) event.preventDefault()
      const damped = Math.min(MAX_VISUAL_PULL_PX, delta * RESISTANCE)
      distanceRef.current = damped
      setDragging(true)
      setPull(damped)
      setArmed(damped >= PULL_THRESHOLD_PX)
    }

    function onTouchEnd() {
      if (startY.current === null) return
      const shouldRefresh =
        !disabled &&
        !refreshing &&
        atPageTop(element, contained) &&
        distanceRef.current >= PULL_THRESHOLD_PX
      startY.current = null
      startX.current = null
      distanceRef.current = 0
      setDragging(false)
      setArmed(false)
      if (shouldRefresh) {
        setPull(SETTLED_PULL_PX)
        onRefreshRef.current()
      } else {
        setPull(0)
      }
    }

    function onTouchCancel() {
      reset()
    }

    element.addEventListener('touchstart', onTouchStart, { passive: true })
    element.addEventListener('touchmove', onTouchMove, { passive: false })
    element.addEventListener('touchend', onTouchEnd)
    element.addEventListener('touchcancel', onTouchCancel)
    return () => {
      element.removeEventListener('touchstart', onTouchStart)
      element.removeEventListener('touchmove', onTouchMove)
      element.removeEventListener('touchend', onTouchEnd)
      element.removeEventListener('touchcancel', onTouchCancel)
    }
  }, [contained, disabled, refreshing])

  useEffect(() => {
    if (refreshing || dragging || pull === 0) return
    const timer = window.setTimeout(() => {
      setPull(0)
      setArmed(false)
    }, 220)
    return () => window.clearTimeout(timer)
  }, [dragging, pull, refreshing])

  const activePull = refreshing ? SETTLED_PULL_PX : pull
  const progress = Math.min(1, activePull / PULL_THRESHOLD_PX)
  const visible = refreshing || activePull > 0
  const statusText = refreshing
    ? t(refreshingLabelKey)
    : armed
      ? t(releaseLabelKey)
      : t(pullLabelKey)
  const offset = refreshing ? 8 : Math.max(-48, activePull - 48)

  return (
    <div
      ref={(node) => {
        rootRef.current = node
        if (scrollRef) scrollRef.current = node
      }}
      data-testid={testId}
      className={`ptr-root ${className}`.trim()}
    >
      <div
        className="ptr-indicator"
        style={{
          transform: `translate(-50%, ${offset}px)`,
          opacity: visible ? 1 : 0,
          transition: dragging ? 'none' : undefined,
        }}
        aria-hidden={!visible}
      >
        <span className="ptr-bubble">
          <svg
            className="ptr-ring"
            viewBox="0 0 40 40"
            aria-hidden="true"
            focusable="false"
          >
            <circle className="ptr-ring__track" cx="20" cy="20" r={RING_RADIUS} />
            <circle
              className="ptr-ring__fill"
              cx="20"
              cy="20"
              r={RING_RADIUS}
              style={{
                strokeDashoffset: RING_CIRCUMFERENCE * (1 - progress),
              }}
            />
          </svg>
          {refreshing ? (
            <span className="skeleton skeleton--inline" aria-hidden="true" />
          ) : (
            <ArrowDown
              size={16}
              weight="bold"
              aria-hidden="true"
              className="ptr-arrow"
              style={{ transform: `rotate(${armed ? 180 : 0}deg)` }}
            />
          )}
        </span>
        <span className="ptr-label">{statusText}</span>
      </div>
      <div
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid={`${testId}-status`}
      >
        {visible ? statusText : ''}
      </div>
      <div
        className="ptr-content"
        data-testid={`${testId}-content`}
        style={{
          transform:
            activePull > 0
              ? `translate3d(0, ${activePull}px, 0)`
              : undefined,
          transition: dragging ? 'none' : undefined,
          willChange: activePull > 0 ? 'transform' : undefined,
        }}
      >
        {children}
      </div>
    </div>
  )
}
